/**
 * M-Pesa Payment page — initiate payment for appointment.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { paymentAPI } from '../../services/api';
import { useNotifications } from '../../contexts/NotificationsContext';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('appointment') || '';
  const amount = searchParams.get('amount') || '500';

  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'pending' | 'success' | 'failed'
  const [paymentId, setPaymentId] = useState(null);
  const [failureReason, setFailureReason] = useState('');
  const [failureCode, setFailureCode] = useState('');
  const hasShownSuccessRef = useRef(false);
  const hasShownFailureRef = useRef(false);
  const { on } = useNotifications();

  const handlePay = async (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      toast.error('Please enter your M-Pesa phone number.');
      return;
    }
    setLoading(true);
    setPaymentStatus('pending');
    setFailureReason('');
    setFailureCode('');
    try {
      const res = await paymentAPI.initiate({
        appointment_id: appointmentId,
        phone_number: phoneNumber,
        amount: parseFloat(amount),
      });
      setPaymentId(res.data.payment_id);
      toast.success('STK Push sent! Check your phone for the M-Pesa prompt.');
    } catch (err) {
      setPaymentStatus('failed');
      const errorMessage = typeof err === 'string'
        ? err
        : (err?.error || err?.message || 'Payment initiation failed.');
      const errorCode =
        err?.data?.result_code ||
        err?.data?.ResponseCode ||
        err?.data?.errorCode ||
        '';
      setFailureReason(errorMessage);
      setFailureCode(errorCode ? String(errorCode) : '');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async (silent = false) => {
    if (!paymentId) return;
    try {
      const res = await paymentAPI.getStatus(paymentId);
      const status = res.data?.status || 'pending';
      if (status === 'completed') {
        setPaymentStatus('success');
        setFailureReason('');
        setFailureCode('');
        if (!hasShownSuccessRef.current) {
          toast.success('Payment successful!');
          hasShownSuccessRef.current = true;
        }
      } else if (status === 'failed') {
        setPaymentStatus('failed');
        const backendCode =
          res.data?.result_code ||
          res.data?.mpesa_query_result?.ResultCode ||
          '';
        const backendReason =
          res.data?.result_description ||
          res.data?.mpesa_query_result?.ResultDesc ||
          'Payment failed. Please try again.';
        setFailureCode(backendCode ? String(backendCode) : '');
        setFailureReason(backendReason);
        if (!hasShownFailureRef.current) {
          toast.error(backendReason);
          hasShownFailureRef.current = true;
        }
      } else if (!silent) {
        toast('Payment still processing...', { icon: '⏳' });
      }
    } catch {
      if (!silent) {
        toast.error('Unable to check payment status.');
      }
    }
  };

  useEffect(() => {
    hasShownSuccessRef.current = false;
    hasShownFailureRef.current = false;
  }, [paymentId]);

  useEffect(() => {
    if (paymentStatus !== 'pending' || !paymentId) return undefined;

    const intervalId = setInterval(() => {
      checkStatus(true);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [paymentStatus, paymentId]);

  // Listen for real-time WebSocket payment_confirmed event
  useEffect(() => {
    if (!paymentId) return undefined;
    const unsub = on('payment_confirmed', (payload) => {
      if (payload?.payment_id === paymentId || paymentStatus === 'pending') {
        setPaymentStatus('success');
        setFailureReason('');
        setFailureCode('');
        if (!hasShownSuccessRef.current) {
          toast.success('Payment successful!');
          hasShownSuccessRef.current = true;
        }
      }
    });
    return unsub;
  }, [paymentId, on, paymentStatus]);

  return (
    <Layout title="Payment" role="patient" showBack>
      <div className="p-4 pb-24">
        {/* M-Pesa branding */}
        <div className="card bg-green-50 border-green-200 text-center mb-4">
          <div className="text-3xl mb-2">📱</div>
          <h2 className="text-lg font-bold text-green-800">M-Pesa Payment</h2>
          <p className="text-sm text-green-600">Lipa Na M-Pesa — Secure & Instant</p>
        </div>

        {/* Payment amount */}
        <div className="card text-center mb-4">
          <p className="text-sm text-gray-500">Amount to Pay</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">KES {parseFloat(amount).toLocaleString()}</p>
          {appointmentId && (
            <p className="text-xs text-gray-400 mt-1">Appointment consultation fee</p>
          )}
        </div>

        {paymentStatus === 'success' ? (
          <div className="card text-center bg-green-50 border-green-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-green-800">Payment Successful!</h3>
            <p className="text-sm text-green-600 mt-1">Your appointment has been confirmed.</p>
            <button onClick={() => navigate('/patient/appointments')} className="btn-primary w-full mt-4">
              View Appointments
            </button>
          </div>
        ) : (
          <form onSubmit={handlePay} className="card space-y-4">
            <div>
              <label className="label">M-Pesa Phone Number *</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                className="input-field"
                placeholder="0712345678"
                required
                disabled={paymentStatus === 'pending'}
              />
              <p className="text-xs text-gray-400 mt-1">You'll receive an STK Push prompt on this number</p>
            </div>

            {paymentStatus !== 'pending' ? (
              <button type="submit" disabled={loading} className="w-full py-3 rounded-xl font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                {loading && <div className="spinner w-5 h-5 border-2"></div>}
                Pay with M-Pesa
              </button>
            ) : (
              <div className="space-y-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                  <div className="spinner w-8 h-8 border-3 border-yellow-500 mx-auto mb-3"></div>
                  <p className="text-sm font-medium text-yellow-800">Waiting for M-Pesa confirmation...</p>
                  <p className="text-xs text-yellow-600 mt-1">Check your phone and enter your M-Pesa PIN</p>
                </div>
                <button type="button" onClick={checkStatus} className="btn-primary w-full">
                  Check Payment Status
                </button>
                <button type="button" onClick={() => { setPaymentStatus(null); setPaymentId(null); setFailureReason(''); setFailureCode(''); hasShownSuccessRef.current = false; hasShownFailureRef.current = false; }} className="btn-secondary w-full">
                  Try Again
                </button>
              </div>
            )}
          </form>
        )}

        {paymentStatus === 'failed' && (
          <div className="card bg-red-50 border-red-200 text-center mt-4">
            <p className="text-sm text-red-700 font-medium">{failureReason || 'Payment failed. Please try again.'}</p>
            {failureCode && (
              <p className="text-xs text-red-600 mt-1">M-Pesa ResultCode: {failureCode}</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentPage;
